import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Bell } from 'lucide-react';
import TruckSchedulerLayout from './TruckSchedulerLayout';
import TruckEditor from './NewTruckEditor';
import { useAuth } from '../../../../context/AuthContext';
import { useSocket } from '../../../../hooks/useSocket';
import { useTimezone } from '../../../../hooks/useTimezone';
import { debugDate } from './utils/dateUtils';
import { determineCurrentStatus, isBackwardMovement } from './utils/statusUtils';
import './TruckEditor.css';

/**
 * Base Dashboard component that can be used for both inbound and outbound dashboards
 * Handles common state management, API calls, and layout
 */
const BaseDashboard = ({ 
  initialView,             // Initial view to display (calendar, operations, history)
  mode,                    // 'inbound' or 'outbound'
  apiEndpoint,             // API endpoint to fetch trucks from
  scheduleFormComponent,   // Modal component for scheduling new trucks
  viewComponents,          // Object mapping view keys to components
  calculateStats,          // Function to calculate dashboard stats from truck data  
  socketEventName,         // Socket event name to listen for updates
}) => {
  // State for showing the schedule new form
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  
  // State for pre-selected date when scheduling from calendar
  const [preSelectedDate, setPreSelectedDate] = useState(null);
  
  // State for truck data from API
  const [truckData, setTruckData] = useState([]);
  
  // State for truck editor
  const [showTruckEditor, setShowTruckEditor] = useState(false);
  const [editingTruck, setEditingTruck] = useState(null);
  
  // State for loading and error handling
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Get current location to determine which view to show
  const location = useLocation();
  
  // Get auth context for token access
  const { currentUser } = useAuth();
  
  // Initialize socket connection
  const { socket, isConnected } = useSocket(currentUser?.id);
  
  // Get user's timezone for date handling
  const userTimezone = useTimezone();
  
  // Log user timezone for debugging
  useEffect(() => {
    console.log(`BaseDashboard using timezone: ${userTimezone} for user: ${currentUser?.username || 'unknown'}`);
  }, [userTimezone, currentUser]);

  // Load truck data from API
  const loadTruckData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || '/api';
      
      // Calculate date 90 days ago for rolling history
      const ninetyDaysAgo = new Date();
      ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
      const startDate = ninetyDaysAgo.toISOString().split('T')[0]; // YYYY-MM-DD format
      
      // Request large limit and date filter to get 90 days of data
      const queryParams = new URLSearchParams({
        limit: '1000', // Large limit to ensure we get all records in 90 days
        start_date: startDate
      });
      
      const response = await fetch(`${API_URL}/${apiEndpoint}?${queryParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to load ${mode} truck data: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      setTruckData(data.trucks || []);
    } catch (err) {
      console.error(`Error loading ${mode} truck data:`, err);
      setError(`Failed to load ${mode} truck data. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadTruckData();
  }, []);

  // Listen for socket events related to truck scheduling
  useEffect(() => {
    if (!socket || !socketEventName) return;
    
    // Authenticate socket with user ID
    if (currentUser?.id && isConnected) {
      socket.emit('authenticate', currentUser.id);
    }
    
    // Listen for truck schedule updates
    socket.on(socketEventName, (updatedTruck) => {
      if (updatedTruck.deleted) {
        // Handle deleted truck
        setTruckData(prevData => prevData.filter(truck => truck.id !== updatedTruck.id));
        return;
      }

      // Log the updated truck for debugging
      console.log(`BaseDashboard socket update received:`, updatedTruck);

      setTruckData(prevData => {
        // Replace the updated truck in the array if it exists, otherwise add it
        const exists = prevData.some(truck => truck.id === updatedTruck.id);
        if (exists) {
          return prevData.map(truck =>
            truck.id === updatedTruck.id ? updatedTruck : truck
          );
        } else {
          return [...prevData, updatedTruck];
        }
      });

      // Call loadTruckData to ensure we get the latest data from the server
      // This helps ensure cancelled trucks appear in the history view
      if (updatedTruck.status === 'Cancelled' || updatedTruck.cancelled_at) {
        console.log('BaseDashboard: Cancelled truck detected, refreshing data to update history view');
        loadTruckData();
      }
    });
    
    return () => {
      socket.off(socketEventName);
    };
  }, [socket, currentUser, isConnected, socketEventName]);

  // Handle saving a new scheduled truck
  const handleSaveSchedule = async (newTruck) => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log(`[${new Date().toISOString()}] BaseDashboard.handleSaveSchedule: Scheduling ${mode} truck with data:`, newTruck);
      
      // Make sure we have required fields and proper format
      if (!newTruck.business_unit || !newTruck.carrier || !newTruck.scheduled_at) {
        console.error('Missing required fields:', { 
          business_unit: newTruck.business_unit, 
          carrier: newTruck.carrier, 
          scheduled_at: newTruck.scheduled_at 
        });
        const validationErrors = {};
        if (!newTruck.business_unit) validationErrors.business_unit = 'Business unit is required';
        if (!newTruck.carrier) validationErrors.carrier = 'Carrier is required';
        if (!newTruck.scheduled_at) validationErrors.scheduled_at = 'Scheduled date/time is required';
        validationErrors.general = 'Missing required fields for scheduling';
        return { success: false, errors: validationErrors };
      }

      // Make sure status is set
      if (!newTruck.status) {
        newTruck.status = 'Scheduled';
      }
      
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || '/api';
      
      const url = `${API_URL}/${apiEndpoint}`;
      console.log(`Sending POST request to: ${url}`);
      
      // Debug output the final payload
      console.log('Final payload being sent to server:', JSON.stringify(newTruck, null, 2));
      
      // Debug scheduled date with timezone information
      if (newTruck.scheduled_at) {
        console.log('Scheduled date debug:', debugDate(newTruck.scheduled_at, userTimezone));
      }
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newTruck)
      });
      
      if (!response.ok) {
        let errorMessage = `Failed to schedule ${mode} truck: ${response.status} ${response.statusText}`;
        let errorData = {};
        
        try {
          errorData = await response.json();
          console.error("Server response error:", errorData);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (e) {
          console.error("Could not parse error response:", e);
        }
        
        // Check for permission errors
        if (response.status === 403) {
          errorMessage = `Permission denied: You don't have permission to schedule ${mode} trucks.`;
          // Add permission errors to validation errors
          const validationErrors = {
            general: errorMessage
          };
          return { success: false, errors: validationErrors };
        }
        
        // If we have field-specific errors
        if (errorData.errors && typeof errorData.errors === 'object') {
          return { success: false, errors: { ...errorData.errors, general: errorMessage } };
        }
        
        // Create custom error with both message and additional data
        const validationErrors = { general: errorMessage };
        
        // Add the required fields to the error object if available
        if (errorData.required && Array.isArray(errorData.required)) {
          errorData.required.forEach(field => {
            validationErrors[field] = `${field.replace('_', ' ')} is required`;
          });
        }
        
        return { success: false, errors: validationErrors };
      }
      
      const data = await response.json();
      console.log(`${mode} truck scheduled successfully:`, data);
      
      // Add the new truck to our state
      if (data && data.truck) {
        setTruckData(prevData => [...prevData, data.truck]);
      }
      
      // Refresh data to ensure we have the latest
      loadTruckData();
      
      // Close the form
      setShowScheduleForm(false);
      setPreSelectedDate(null); // Reset pre-selected date after scheduling
      return true;
    } catch (err) {
      console.error(`Error scheduling ${mode} truck:`, err);
      
      // Parse the error response to extract field-specific errors
      let errorMessage = `Failed to schedule ${mode} truck: ` + err.message;
      let validationErrors = {
        general: errorMessage
      };
      
      try {
        // If the error contains structured data about required fields
        if (err.required && Array.isArray(err.required)) {
          // Map each required field to a validation error
          err.required.forEach(field => {
            validationErrors[field] = `${field.replace('_', ' ')} is required`;
          });
        }
      } catch (parseErr) {
        console.error('Error parsing validation errors:', parseErr);
      }
      
      // Return validation errors to display in the form
      return { success: false, errors: validationErrors };
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle updating an existing truck
  const handleUpdateTruck = async (updatedTruck) => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log(`[${new Date().toISOString()}] BaseDashboard.handleUpdateTruck: Updating ${mode} truck with data:`, updatedTruck);
      
      // Implement debounce to prevent race conditions
      if (window.__lastTruckUpdateTimestamp && 
          Date.now() - window.__lastTruckUpdateTimestamp < 1000) {
        console.log('BaseDashboard: Update throttled to prevent race condition');
        // Wait for a short delay before processing the update
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      
      // Record the timestamp of this update
      window.__lastTruckUpdateTimestamp = Date.now();
      
      // Basic validation
      if (!updatedTruck.id) {
        console.error('Missing truck ID for update');
        return { success: false, errors: { general: 'Missing truck ID for update' } };
      }
      
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || '/api';
      
      // Deep copy the truck to avoid reference issues
      const processedTruck = JSON.parse(JSON.stringify(updatedTruck));
      
      // Get the original truck from current data
      const originalTruck = truckData.find(t => t.id === updatedTruck.id);
      if (!originalTruck) {
        console.error('BaseDashboard: Original truck not found in current data');
        return { success: false, errors: { general: 'Truck not found in current data' } };
      }
      
      // Determine current and new status explicitly from the status field
      // Both inbound and outbound trucks now have a dedicated status field
      
      // Get current status from the original truck
      const currentStatus = originalTruck.status || determineCurrentStatus(originalTruck, mode);
      
      // Get the new status from the processed truck
      const newStatus = processedTruck.status || determineCurrentStatus(processedTruck, mode);
      
      // Compare normalized forms of the status values for case-insensitive comparison
      const normalizedCurrentStatus = currentStatus.toLowerCase();
      const normalizedNewStatus = newStatus.toLowerCase();
      
      // Check if status has actually changed
      const statusChanged = normalizedCurrentStatus !== normalizedNewStatus;
      
      // If status changed, determine if it's a backward movement in the status sequence
      // Use the helper function from statusUtils.js
      const isBackward = statusChanged ? isBackwardMovement(originalTruck, newStatus, mode) : false;
      
      console.log(`BaseDashboard: Status analysis - current: ${currentStatus}, new: ${newStatus}, normalized current: ${normalizedCurrentStatus}, normalized new: ${normalizedNewStatus}, changed: ${statusChanged}, backward: ${isBackward}`);
      
      // Throttle status changes to prevent race conditions
      if (statusChanged) {
        // Add artificial delay for any status change operation to ensure sequencing
        console.log('BaseDashboard: Adding delay for status change operation');
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      if (statusChanged && isBackward) {
        // This is a backward status movement - use our specialized status service
        console.log(`BaseDashboard: Using standardized backward status movement for ${mode} truck #${updatedTruck.id}`);
        
        try {
          // Import truckStatusService
          const truckStatusService = await import('../../../../services/truckStatusService').then(module => module.default);
          
          // Make sure the truck object has the correct status before sending
          const truckWithStatus = {
            ...processedTruck,
            status: currentStatus  // Use the current status as determined by BaseDashboard
          };

          console.log(`BaseDashboard: Sending truck with explicit status ${currentStatus} for backward movement`);

          // Use the standardized moveTruckBackward function
          const result = await truckStatusService.moveTruckBackward(
            mode,
            updatedTruck.id,
            newStatus,   // Target status
            truckWithStatus // Truck object with explicit current status
          );
          
          console.log(`BaseDashboard: Standardized status movement successful:`, result);
          
          // Update the truck in our state
          if (result && result.truck) {
            setTruckData(prevData => 
              prevData.map(truck => truck.id === result.truck.id ? result.truck : truck)
            );
          }
          
          // Refresh data to ensure we have the latest
          loadTruckData();
          
          // Close the editor
          setShowTruckEditor(false);
          setEditingTruck(null);
          return true;
        } catch (error) {
          console.error(`BaseDashboard: Error in standardized status movement:`, error);
          
          let errorMessage = `Failed to update truck status: ${error.message}`;
          
          // Provide more helpful messages for specific errors
          if (error.message.includes('Invalid status movement')) {
            errorMessage = `Cannot change status to ${newStatus}: this is not a valid movement.`;
          } else if (error.message.includes('Permission')) {
            errorMessage = `You don't have permission to change truck status. Please contact an administrator.`;
          }
          
          return { 
            success: false, 
            errors: { 
              general: errorMessage
            } 
          };
        }
      } else {
        // Standard update flow - no status change or forward movement
        const url = `${API_URL}/${apiEndpoint}/${updatedTruck.id}`;
        console.log(`Sending standard PUT request to: ${url}`);

        // For outbound trucks, ensure status field has proper capitalization
        if (mode === 'outbound' && processedTruck.status) {
          if (typeof processedTruck.status === 'string' && processedTruck.status.length > 0) {
            // Only apply case normalization if the status isn't already properly cased
            if (processedTruck.status !== processedTruck.status.charAt(0).toUpperCase() + processedTruck.status.slice(1).toLowerCase()) {
              const firstChar = processedTruck.status.charAt(0).toUpperCase();
              const restOfStatus = processedTruck.status.slice(1).toLowerCase();
              processedTruck.status = firstChar + restOfStatus;
              console.log(`BaseDashboard: Normalized outbound status to: ${processedTruck.status}`);
            }
          }

          // Ensure status timestamps are set when changing status
          if (statusChanged) {
            console.log(`BaseDashboard: Ensuring timestamp for status ${processedTruck.status}`);

            // Map statuses to their timestamp fields for outbound
            const outboundStatusTimestamps = {
              'Scheduled': 'scheduled_at',
              'Staged': 'staged_at',
              'Loaded': 'loaded_at',
              'Shipped': 'shipped_at',
              'Completed': 'completed_at'
            };

            // Get the corresponding timestamp field for this status
            const timestampField = outboundStatusTimestamps[processedTruck.status];

            // If we have a timestamp field and it's not set, set it
            if (timestampField && !processedTruck[timestampField]) {
              processedTruck[timestampField] = new Date().toISOString();
              console.log(`BaseDashboard: Set ${timestampField} timestamp to ${processedTruck[timestampField]}`);
            }
          }
        }
        
        // Debug output the final payload
        console.log('Standard update payload being sent to server:', JSON.stringify(processedTruck, null, 2));
        
        const response = await fetch(url, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(processedTruck)
        });
        
        if (!response.ok) {
          let errorMessage = `Failed to update ${mode} truck: ${response.status} ${response.statusText}`;
          let errorData = {};
          
          try {
            errorData = await response.json();
            console.error("Server response error:", errorData);
            if (errorData.message) {
              errorMessage = errorData.message;
            }
          } catch (e) {
            console.error("Could not parse error response:", e);
          }
          
          // Handle various error cases
          if (response.status === 403) {
            errorMessage = `Permission denied: You don't have permission to update ${mode} trucks.`;
          }
          
          return { 
            success: false, 
            errors: { 
              ...(errorData.errors || {}), 
              general: errorMessage 
            } 
          };
        }
        
        const data = await response.json();
        console.log(`${mode} truck updated successfully:`, data);

        // Update the truck in our state
        if (data && data.truck) {
          setTruckData(prevData =>
            prevData.map(truck => truck.id === data.truck.id ? data.truck : truck)
          );

          // Special handling for cancelled and completed trucks to ensure they appear in history
          if (data.truck.status === 'Cancelled' || data.truck.status === 'Completed') {
            console.log(`BaseDashboard: ${data.truck.status} truck detected, ensuring history view is updated`);

            // Wait a moment before refreshing data to ensure backend processing is complete
            setTimeout(() => {
              loadTruckData();
            }, 500);
          }
        }

        // Refresh data to ensure we have the latest
        loadTruckData();

        // Close the editor
        setShowTruckEditor(false);
        setEditingTruck(null);
        return true;
      }
    } catch (err) {
      console.error(`Error updating ${mode} truck:`, err);
      return { 
        success: false, 
        errors: { 
          general: `Failed to update ${mode} truck: ${err.message}` 
        } 
      };
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate dashboard stats
  const dashboardStats = calculateStats ? calculateStats(truckData) : {};
  

  // Function to open the edit modal for a truck
  const openTruckEditor = (truck, options = {}) => {
    setEditingTruck(truck);

    // If status change is requested, we need to pass it to StatusChangeConfirmation
    // This is for the special case of BOL upload when moving from Shipped to Completed
    if (options && options.statusChange) {
      // Store status change as part of the editing truck data
      setEditingTruck({
        ...truck,
        __statusChangeRequest: options.statusChange
      });
    }

    setShowTruckEditor(true);
  };
  
  // Determine which view to show based on initialView prop or current URL
  const getActiveView = () => {
    // First check if initialView is provided (for handling page refreshes)
    if (initialView) {
      return initialView === 'calendar' ? 'month' : initialView;
    }
    
    // Fallback to checking URL path
    const path = location.pathname;
    if (path.includes('receiving')) return 'receiving';
    if (path.includes('shipping')) return 'shipping';
    if (path.includes('history')) return 'history';
    return 'month'; // default to month view
  };
  
  const activeView = getActiveView();

  // Render the main content based on active view
  const renderContent = () => {
    // Display error message if any
    if (error) {
      return (
        <div className="m-4 p-3 bg-red-900/30 border border-red-800 rounded-lg text-red-200 flex-shrink-0">
          {error}
        </div>
      );
    }

    if (isLoading && truckData.length === 0) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="flex flex-col items-center">
            <div className={`w-12 h-12 border-4 border-${mode === 'inbound' ? 'blue' : 'green'}-400 border-t-transparent rounded-full animate-spin mb-3`}></div>
            <div className="text-slate-400">Loading {mode} truck data...</div>
          </div>
        </div>
      );
    }

    // Get the component for the active view
    const ViewComponent = viewComponents[activeView];
    
    // If component doesn't exist, show an error
    if (!ViewComponent) {
      return (
        <div className="m-4 p-3 bg-yellow-900/30 border border-yellow-800 rounded-lg text-yellow-200">
          View "{activeView}" not found
        </div>
      );
    }
    
    // Render the appropriate view with props
    return (
      <ViewComponent 
        truckData={truckData}
        setTruckData={setTruckData}
        refreshData={loadTruckData}
        isLoading={isLoading}
        socket={socket}
        isConnected={isConnected}
        setShowScheduleForm={setShowScheduleForm}
        setPreSelectedDate={setPreSelectedDate}
        openTruckEditor={openTruckEditor}
        mode={mode}
      />
    );
  };

  // Create a form component with passed props
  const ScheduleForm = scheduleFormComponent;

  return (
    <TruckSchedulerLayout mode={mode}>
      <div className="h-full flex flex-col overflow-hidden">
        {/* Main content area */}
        <div className="flex-1 overflow-hidden">
          {renderContent()}
        </div>
      </div>
      
      {/* Schedule New Form Modal */}
      {showScheduleForm && ScheduleForm && (
        <ScheduleForm 
          onClose={() => setShowScheduleForm(false)} 
          onSave={handleSaveSchedule}
          preSelectedDate={preSelectedDate}
          isLoading={isLoading}
          truckData={truckData}
        />
      )}
      
      {/* Truck Editor Modal */}
      {showTruckEditor && editingTruck && (
        <TruckEditor
          onClose={() => {
            setShowTruckEditor(false);
            setEditingTruck(null);
          }}
          onSave={handleUpdateTruck}
          truckData={editingTruck}
          mode={mode}
          title={`Edit ${mode === 'inbound' ? 'Inbound' : 'Outbound'} Truck`}
        />
      )}
    </TruckSchedulerLayout>
  );
};

export default BaseDashboard;